import {
  BlockOutlined,
  ContentCopyOutlined,
  MoreVert,
  ReplayOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";

const STATUS_CHIP = {
  ACTIVE: { label: "Active", color: "success" },
  REVOKED: { label: "Revoked", color: "default" },
  DISABLED: { label: "Disabled", color: "error" },
  SUPERSEDED: { label: "Superseded", color: "info" },
};

export const ShareLinkStatusChip = ({ status }) => {
  const cfg = STATUS_CHIP[status] || { label: status || "Unknown", color: "default" };
  return (
    <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" sx={{ height: 22 }} />
  );
};

const copyUrl = async (url) => {
  await navigator.clipboard.writeText(url);
  toast.success("Link copied.");
};

/**
 * Three-dot action menu for a permanent document share link row.
 * Pass student-scoped or org-wide API handlers via props.
 */
const DocumentShareLinkActionsMenu = ({
  link,
  busy,
  onBusyChange,
  onRefresh,
  setRevoked,
  regenerate,
}) => {
  const [anchor, setAnchor] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const dead = link.documentPresent === false;
  const disabled = link.permanentlyDisabled;
  const canCopy = !link.revoked && !dead;
  const canReEnable = link.revoked && !disabled && !dead;
  const canRevoke = !link.revoked && !dead;
  const canRegenerate = !link.supersededByTokenId && !dead;

  const run = async (action) => {
    onBusyChange(link.id);
    try {
      await action();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Action failed.");
    } finally {
      onBusyChange(null);
      setConfirm(null);
      setAnchor(null);
    }
  };

  const handleRegenerate = (revokeOld) =>
    run(async () => {
      const result = await regenerate(link.id, revokeOld);
      const url = result?.publicUrl;
      toast.success(revokeOld ? "Link revoked and regenerated." : "New link created.");
      if (url) {
        try {
          await copyUrl(url);
        } catch {
          toast.info("New link ready — copy it from the list.");
        }
      }
    });

  return (
    <>
      <Tooltip title="Actions">
        <span>
          <IconButton
            size="small"
            disabled={busy === link.id}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            {busy === link.id ? <CircularProgress size={16} /> : <MoreVert fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem disabled={!canCopy} onClick={() => run(() => copyUrl(link.publicUrl))}>
          <ListItemIcon>
            <ContentCopyOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!canRevoke}
          onClick={() =>
            run(async () => {
              await setRevoked(link.id, true);
              toast.success("Link revoked.");
            })
          }
        >
          <ListItemIcon>
            <BlockOutlined fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Revoke</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!canReEnable}
          onClick={() =>
            run(async () => {
              await setRevoked(link.id, false);
              toast.success("Link re-enabled.");
            })
          }
        >
          <ListItemIcon>
            <ReplayOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-enable</ListItemText>
        </MenuItem>
        <MenuItem disabled={!canRegenerate} onClick={() => setConfirm({ type: "f1" })}>
          <ListItemIcon>
            <BlockOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Revoke &amp; regenerate</ListItemText>
        </MenuItem>
        <MenuItem disabled={!canRegenerate} onClick={() => setConfirm({ type: "f2" })}>
          <ListItemIcon>
            <SyncOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Regenerate (keep old working)</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirm?.type === "f1" ? "Revoke & regenerate?" : "Regenerate link?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="text-app-text-muted">
            {confirm?.type === "f1"
              ? "The old link stops working immediately and can never be re-enabled. A new link is created for future emails."
              : "A new link is created for future emails; the old link keeps working until you revoke it."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleRegenerate(confirm?.type === "f1")}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentShareLinkActionsMenu;
